import { GameIdsMap } from "abstract/constants/game-ids"
import {
  SpecialEventNotificationPreset,
  SpecialEventPresetNotificationDefaults,
  SpecialEventPresetsConfigType,
} from "domains/managingJson/store/types"

const defaultNotification: SpecialEventPresetNotificationDefaults = {
  enabled: true,
  spawnProbability: 1,
  ingameOverlayType: "stacking",
  buffsReward: 0.1,
  duration: 10,
  triggerDelay: 0,
  playSound: false,
}

const defaultStyle = {
  position: "fixed",
  left: 0,
  top: "50%",
  transform: "translateY(-50%)",
  width: "20vw",
  maxWidth: 600,
  minWidth: 220,
}

const event = (
  triggerEventName: string,
  friendlyName: string,
  description: string,
  options: Partial<SpecialEventNotificationPreset> = {},
): SpecialEventNotificationPreset => ({
  triggerEventName,
  friendlyName,
  description,
  source: "code-and-sheet",
  codeSupported: true,
  trackingName: options.trackingName,
  aliases: options.aliases,
  recommendationScore: options.recommendationScore,
  emotion: options.emotion,
  comment: options.comment,
  asset: {
    required: true,
    scope: "global",
    aliases: options.asset?.aliases ?? [friendlyName, triggerEventName],
  },
  defaults: options.defaults,
})

// Events Overwolf delivers (or will) but the shipped client does not emit yet. Shipped disabled so a
// campaign cannot be configured against a trigger that would silently never fire.
const pending = (
  triggerEventName: string,
  friendlyName: string,
  description: string,
  options: Partial<SpecialEventNotificationPreset> = {},
): SpecialEventNotificationPreset => ({
  ...event(triggerEventName, friendlyName, description, options),
  source: "manual",
  codeSupported: false,
  defaults: { ...options.defaults, enabled: false },
})

const timeElapsed15 = () => pending(
  "time_elapsed_15",
  "15 Seconds Into the Match",
  "[NEEDS RELEASE] 15 seconds after the match started",
  {
    trackingName: "TimeElapsed15",
    recommendationScore: "5 - Always recommended",
    aliases: ["time elapsed 15", "time_elapsed_15", "15 seconds"],
    comment:
      "The tick-worker timer exists only in the contest notification service. The special-events path has "
      + "after_launch_<N>s, which counts from game launch and caps at 30s. Parametric once ported.",
    defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
  },
)

const KILL_FEED_NOTE
  = "Derived from the in-game kill feed text, so it only fires on English-language clients."

const weaponKill = (
  triggerEventName: string,
  friendlyName: string,
  weapon: string,
  trackingName: string,
  buffsReward = 0.1,
) => event(triggerEventName, friendlyName, `When the player eliminates an opponent with ${weapon}`, {
  trackingName,
  aliases: [friendlyName.toLowerCase(), triggerEventName],
  comment: KILL_FEED_NOTE,
  defaults: { buffsReward, duration: 10, triggerDelay: 0 },
})

const matchStart = (description = "A match is started") => event("match_start", "Match Start", description, {
  trackingName: "MatchStart",
  recommendationScore: "5 - Always recommended",
  aliases: ["match start", "matchstart", "start"],
  defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 9 },
})

const matchWon = (available = true) => (available ? event : pending)("match_won", "Victory", "When a user won a match", {
  trackingName: "Victory",
  recommendationScore: "5 - Always recommended",
  aliases: ["victory", "match won", "match end win", "match end won", "win", "won"],
  defaults: { buffsReward: 0.5, duration: 10, triggerDelay: 7 },
})

const matchLost = (available = true) => (available ? event : pending)("match_lost", "Match Lost", "When a user lost a match", {
  trackingName: "MatchLost",
  recommendationScore: "4 - Strong candidate",
  aliases: ["match lost", "match end lost", "loss", "lost", "defeat"],
  defaults: { buffsReward: 0, duration: 10, triggerDelay: 7 },
})

const matchEnd = () => event("match_end", "Match End", "A match ended regardless of outcome", {
  trackingName: "MatchEnd",
  recommendationScore: "5 - Always recommended",
  aliases: ["match end", "matchend", "end"],
  defaults: { buffsReward: 0, duration: 10, triggerDelay: 7 },
})

const kill = (triggerEventName = "kill", friendlyName = "Kill") => event(
  triggerEventName,
  friendlyName,
  "When a player eliminates an opponent",
  {
    trackingName: "Kill",
    recommendationScore: "4 - Strong candidate",
    aliases: ["kill", "elimination", "eliminations", friendlyName],
    defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
  },
)

const assist = () => event("assist", "Assist", "When a user assists in a kill", {
  trackingName: "Assist",
  recommendationScore: "4 - Strong candidate",
  aliases: ["assist", "assists"],
  defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
})

const death = () => event("death", "Death", "When a user is killed", {
  trackingName: "Death",
  recommendationScore: "3 - Situational",
  aliases: ["death", "died", "killed"],
  defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
})

const survived = (seconds: 300 | 600 | 900 | 1200) => {
  const minutes = seconds / 60

  return pending(
    `survived_${seconds}sec`,
    `Survived ${minutes} Minutes`,
    `When the player is still alive ${minutes} minutes after the Fortnite match started`,
    {
      trackingName: `Survived${seconds}Sec`,
      recommendationScore: "4 - Strong candidate",
      aliases: [
        `survived ${seconds}sec`,
        `survived ${seconds} seconds`,
        `survived ${minutes} minutes`,
        `survive ${minutes} minutes`,
      ],
      comment:
        "Merged in buff.app #1676 but not in a tagged client release. Keep disabled until the client also limits "
        + "survival tracking to supported Battle Royale modes and stops the timer on lobby/leave.",
      defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
    },
  )
}

// The whole time-based menu. There is no GEP feed for those titles, so the funnel is
// launch banner -> this notification -> summary banner, and only the middle beat is an event.
// The client already emits after_launch_1s..after_launch_30s, so the offset belongs in the trigger
// name itself: triggerDelay would consume the spawn cap at T+1s and leave a timer that can outlive
// the session. No reward — time-based campaigns are impression-only, and no code path can pay one.
const launchFollowUp = (seconds = 20): SpecialEventNotificationPreset => event(
  `after_launch_${seconds}s`,
  "Launch Follow-up Notification",
  `In-game notification ${seconds}s after the game launches`,
  {
    trackingName: "LaunchFollowUp",
    recommendationScore: "5 - Always recommended",
    aliases: ["launch follow-up", "launch followup", "after launch", "after_launch"],
    comment:
      "Time-based games only. To move the beat, change the number in the trigger name — the client "
      + "emits every whole second from after_launch_1s to after_launch_30s. Leave triggerDelay at 0.",
    // 0, not undefined: the published path reads this from the remote managingJson item, and a
    // JSON round-trip drops undefined-valued keys — the preset would then inherit
    // defaultNotification's 0.1 and configure a reward no code path can pay.
    defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
  },
)

export const DEFAULT_SPECIAL_EVENT_PRESETS: SpecialEventPresetsConfigType = {
  version: 3,
  generatedFrom: [
    "buff.app local code-supported specialEventManager triggerEvent/matchStarted call sites",
    "Available Events for Rewarded Achievements Campaigns (Moments) workbook",
    "Recorded raw GEP sessions 2026-09-01 (Fortnite BR + LEGO + BR, Roblox two mini-games) via DEV2-2170",
    "buff.app #1669, #1675 and #1676 merged trigger implementations (awaiting a tagged client release)",
  ],
  defaultStyle,
  defaultNotification,
  games: {
    [GameIdsMap.FORTNITE]: {
      gameId: GameIdsMap.FORTNITE,
      gameName: "Fortnite",
      trackingName: "Fortnite",
      aliases: ["fortnite", "fn"],
      events: [
        matchStart("A Fortnite match is started"),
        kill("kill", "Elimination"),
        event("knockout", "Knockout", "Triggered when the player knocks down an opponent before final kill", {
          trackingName: "Knockout",
          recommendationScore: "1-2 - Rare use",
          aliases: ["knockout", "knockouts", "knockdown"],
          defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
        }),
        death(),
        event(
          "reached_top_10",
          "Reached Top 10",
          "While still alive, the player's placement reaches the top 10 (top 10 teams in duos and squads)",
          {
            trackingName: "ReachedTop10",
            recommendationScore: "4 - Strong candidate",
            aliases: ["reached top 10", "top 10", "top10"],
            defaults: { buffsReward: 0.3, duration: 10, triggerDelay: 0, cooldown: 60 },
          },
        ),
        matchWon(),
        matchLost(),
        event("fish_collected", "Fish Collected", "When a fish item is collected", {
          trackingName: "FishCollected",
          aliases: ["fish collected", "fish"],
        }),
        event("potion_collected", "Potion Collected", "When a shield/potion item is collected", {
          trackingName: "PotionCollected",
          aliases: ["potion collected", "potion", "shield"],
        }),
        matchEnd(),
        event("hit", "Shot Landed", "When the player lands a shot on an opponent", {
          trackingName: "ShotLanded",
          recommendationScore: "1-2 - Rare use",
          aliases: ["hit", "hits", "shot landed"],
          comment: "Measured 64 per match in a recorded session — pair it with a low spawn probability or a cooldown.",
          defaults: { buffsReward: 0.05, duration: 10, triggerDelay: 0 },
        }),
        event("knockedout", "Knocked Down", "When the local player is knocked down, before elimination", {
          trackingName: "KnockedDown",
          recommendationScore: "3 - Situational",
          aliases: ["knocked down", "knockedout", "dbno", "downed"],
          comment: "Distinct from Knockout, which is the player knocking an opponent down.",
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
        }),
        event("revived", "Revived", "When the local player is revived after being knocked down", {
          trackingName: "Revived",
          recommendationScore: "4 - Strong candidate",
          aliases: ["revived", "revive", "picked up"],
          defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
        }),
        event("emote_start", "Emote", "When the player starts an emote", {
          trackingName: "Emote",
          aliases: ["emote", "emote start", "dance"],
          comment: "Code-supported but not observed in the recorded session, so its rate is unmeasured.",
        }),
        event("airfield", "Pre-Game Island", "The player is on the pre-game island, before the Battle Bus", {
          trackingName: "Airfield",
          aliases: ["airfield", "pre-game island", "pregame"],
          comment: "Phase event, confirmed ~3s after match start.",
        }),
        event("aircraft", "Battle Bus", "The player is on the Battle Bus", {
          trackingName: "BattleBus",
          recommendationScore: "4 - Strong candidate",
          aliases: ["aircraft", "battle bus", "bus"],
          comment: "Phase event, confirmed ~51s after match start — a natural attention window before the drop.",
        }),
        event("freefly", "Skydiving", "The player is skydiving after leaving the Battle Bus", {
          trackingName: "Skydiving",
          aliases: ["freefly", "skydiving", "gliding"],
        }),
        event("landed", "Landed", "The player has landed on the island", {
          trackingName: "Landed",
          recommendationScore: "4 - Strong candidate",
          aliases: ["landed", "landing", "touchdown"],
        }),
        weaponKill("pickaxe_kill", "Pickaxe Elimination", "the pickaxe", "PickaxeKill", 0.3),
        weaponKill("sniper_kill", "Sniper Elimination", "a sniper rifle", "SniperKill", 0.2),
        weaponKill("marksman_kill", "Marksman Rifle Elimination", "a marksman rifle", "MarksmanKill"),
        weaponKill("pistol_kill", "Pistol Elimination", "a pistol", "PistolKill"),
        weaponKill("rifle_kill", "Rifle Elimination", "a rifle", "RifleKill"),
        weaponKill("shotgun_kill", "Shotgun Elimination", "a shotgun", "ShotgunKill"),
        weaponKill("smg_kill", "SMG Elimination", "an SMG", "SmgKill"),
        event("bus_driver_thanked", "Thanked the Bus Driver", "When the player thanks the Battle Bus driver", {
          trackingName: "BusDriverThanked",
          aliases: ["thanked the bus driver", "bus driver"],
          comment: KILL_FEED_NOTE,
        }),
        event("player_rebooted", "Rebooted", "When the player is brought back into the match by a Reboot Van", {
          trackingName: "PlayerRebooted",
          recommendationScore: "4 - Strong candidate",
          aliases: ["rebooted", "reboot", "reboot van"],
          comment: KILL_FEED_NOTE,
          defaults: { buffsReward: 0.3, duration: 10, triggerDelay: 0 },
        }),
        event("ping_100", "High Ping (100ms)", "When the player's ping first crosses 100ms in a match", {
          trackingName: "Ping100",
          aliases: ["ping 100", "high ping", "ping"],
          comment:
            "Parametric: any multiple of 10 works (ping_50, ping_150, ...), once per threshold per match. "
            + "Ping updates were confirmed flowing in both Battle Royale and LEGO Fortnite.",
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
        }),
        event("after_launch_20s", "Launch Follow-up (20s)", "20 seconds after the game was launched", {
          trackingName: "AfterLaunch20s",
          aliases: ["after launch 20s", "launch follow-up"],
          comment:
            "Parametric: after_launch_1s .. after_launch_30s. Counts from game launch rather than match start, "
            + "so its Buffs reward is discarded when the match begins — use it for impressions.",
        }),
        survived(300),
        survived(600),
        survived(900),
        survived(1200),
        pending("double_kill", "Double Kill", "When the player scores two eliminations in quick succession", {
          trackingName: "DoubleKill",
          aliases: ["double kill", "2kill"],
          comment: "Merged in buff.app #1675 but not in a tagged client release. Expected generic payload: '2kill' (not capture-verified).",
          defaults: { buffsReward: 0.2, duration: 10, triggerDelay: 0 },
        }),
        pending("triple_kill", "Triple Kill", "When the player scores three eliminations in quick succession", {
          trackingName: "TripleKill",
          aliases: ["triple kill", "3kill"],
          comment: "Merged in buff.app #1675 but not in a tagged client release. Expected generic payload: '3kill' (not capture-verified).",
          defaults: { buffsReward: 0.3, duration: 10, triggerDelay: 0 },
        }),
        pending("multi_kill", "Multi Kill", "When the player scores four or more eliminations in quick succession", {
          trackingName: "MultiKill",
          aliases: ["multi kill", "mkill"],
          comment: "Merged in buff.app #1675 but not in a tagged client release. Expected generic payload: 'mkill' (not capture-verified).",
          defaults: { buffsReward: 0.4, duration: 10, triggerDelay: 0 },
        }),
        pending("entered_vehicle", "Entered a Vehicle", "When the player gets into a vehicle", {
          trackingName: "EnteredVehicle",
          recommendationScore: "4 - Strong candidate",
          aliases: ["entered vehicle", "car", "driving"],
          comment:
            "Merged in buff.app #1675 but not in a tagged client release. game_info.in_vehicle is best-effort and was "
            + "missing in at least one capture; repeated vehicle transitions can also exhaust the per-game spawn cap.",
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
        }),
        pending("exited_vehicle", "Left a Vehicle", "When the player gets out of a vehicle", {
          trackingName: "ExitedVehicle",
          recommendationScore: "1-2 - Rare use",
          aliases: ["exited vehicle", "left vehicle"],
          comment:
            "Merged in buff.app #1675 but not in a tagged client release. game_info.in_vehicle is best-effort and was "
            + "missing in at least one capture.",
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
        }),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.APEX]: {
      gameId: GameIdsMap.APEX,
      gameName: "Apex Legends",
      trackingName: "Apex",
      aliases: ["apex", "apex legends"],
      events: [
        matchStart("An Apex Legends match is started"),
        matchEnd(),
        kill(),
        assist(),
        event("knockdown", "Knockdown", "When the player knocks down an opponent", {
          trackingName: "Knockdown",
          aliases: ["knockdown", "knockdowns", "knocked"],
          defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
        }),
        event("healed_from_ko", "Revive", "When the player is revived from knock-out", {
          trackingName: "Revive",
          aliases: ["revive", "revived", "healed from ko", "healed_from_ko"],
          defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
        }),
        event("respawn", "Respawn", "When the player respawns", {
          trackingName: "Respawn",
          aliases: ["respawn", "respawned"],
          defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
        }),
        death(),
        matchWon(),
        matchLost(),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.OVERWATCH]: {
      gameId: GameIdsMap.OVERWATCH,
      gameName: "Overwatch 2",
      trackingName: "Overwatch",
      aliases: ["overwatch", "overwatch 2", "ow", "ow2"],
      events: [
        matchStart("An Overwatch match is started"),
        matchEnd(),
        event("elimination", "Elimination", "When the player eliminates an opponent", {
          trackingName: "Elimination",
          recommendationScore: "4 - Strong candidate",
          aliases: ["elimination", "eliminations", "kill", "kills"],
          defaults: { buffsReward: 0.2, duration: 10, triggerDelay: 0 },
        }),
        assist(),
        death(),
        event("objective_kill", "Objective Kill", "When the player gets an objective kill", {
          trackingName: "ObjectiveKill",
          aliases: ["objective kill", "objective_kill"],
        }),
        matchWon(),
        matchLost(),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.RS6]: {
      gameId: GameIdsMap.RS6,
      gameName: "Rainbow Six: Siege",
      trackingName: "RainbowSix",
      aliases: ["rainbow six", "rainbow six siege", "r6", "rs6"],
      events: [
        matchStart("A Rainbow Six match is started from the first supported round"),
        kill(),
        event("headshot", "Headshot", "When a kill is done via headshot", {
          trackingName: "Headshot",
          recommendationScore: "4 - Strong candidate",
          aliases: ["headshot", "headshots"],
          defaults: { buffsReward: 0.2, duration: 10, triggerDelay: 0 },
        }),
        death(),
        event("roundOutcome", "Round Outcome", "When the game reports the round outcome", {
          trackingName: "RoundOutcome",
          aliases: ["round outcome", "roundOutcome", "roundoutcome", "round end"],
          defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
        }),
        event("matchOutcome", "Match Outcome", "When the game reports the match outcome", {
          trackingName: "MatchOutcome",
          aliases: ["match outcome", "matchOutcome", "matchoutcome", "match end"],
          defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 7 },
        }),
        matchWon(false),
        matchLost(false),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.MARVEL_RIVALS]: {
      gameId: GameIdsMap.MARVEL_RIVALS,
      gameName: "Marvel Rivals",
      trackingName: "Marvel",
      aliases: ["marvel", "marvel rivals"],
      events: [
        matchStart("A Marvel Rivals match is started"),
        matchEnd(),
        kill(),
        assist(),
        death(),
        event("round_end", "Round End", "When a round concludes", {
          trackingName: "RoundEnd",
          aliases: ["round end", "round_end"],
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
        }),
        matchWon(),
        matchLost(),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.ARC_RAIDERS]: {
      gameId: GameIdsMap.ARC_RAIDERS,
      gameName: "ARC Raiders",
      trackingName: "ArcRaiders",
      aliases: ["arc", "arc raiders", "arc-raiders"],
      events: [
        { ...matchStart("An ARC Raiders match is started"), source: "manual" },
        { ...matchEnd(), source: "manual" },
        timeElapsed15(),
      ],
    },
    [GameIdsMap.VALORANT]: {
      gameId: GameIdsMap.VALORANT,
      gameName: "Valorant",
      trackingName: "Valorant",
      aliases: ["valorant", "val"],
      events: [
        matchStart("A Valorant match is started"),
        matchEnd(),
        kill(),
        assist(),
        death(),
        event("headshot", "Headshot", "When a kill is done via headshot", {
          trackingName: "Headshot",
          aliases: ["headshot", "headshots"],
          defaults: { buffsReward: 0.1, duration: 10, triggerDelay: 0 },
        }),
        event("round_won", "Round Won", "When a user won a round", {
          trackingName: "RoundWon",
          aliases: ["round won", "round_won"],
        }),
        event("round_lost", "Round Lost", "When a user lost a round", {
          trackingName: "RoundLost",
          aliases: ["round lost", "round_lost"],
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
        }),
        matchWon(),
        matchLost(),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.ROBLOX]: {
      gameId: GameIdsMap.ROBLOX,
      gameName: "Roblox",
      trackingName: "Roblox",
      aliases: ["roblox"],
      events: [
        matchStart("A Roblox game session/match is started"),
        matchEnd(),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.MINECRAFT_PC]: {
      gameId: GameIdsMap.MINECRAFT_PC,
      gameName: "Minecraft Desktop",
      trackingName: "Minecraft",
      aliases: ["minecraft", "minecraft desktop", "minecraft pc"],
      events: [
        matchStart("A Minecraft session/match is started"),
        matchEnd(),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.ROCKET_LEAGUE]: {
      gameId: GameIdsMap.ROCKET_LEAGUE,
      gameName: "Rocket League",
      trackingName: "RocketLeague",
      aliases: ["rocket league", "rocketleague", "rl"],
      events: [
        matchStart("A Rocket League match is started"),
        event("matchEnd", "Match End", "A Rocket League match ended regardless of outcome", {
          trackingName: "MatchEnd",
          recommendationScore: "5 - Always recommended",
          aliases: ["match end", "matchend", "end"],
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 7 },
        }),
        event("victory", "Victory", "When Rocket League reports a victory", {
          trackingName: "Victory",
          aliases: ["victory", "win", "won"],
          defaults: { buffsReward: 0.5, duration: 10, triggerDelay: 7 },
        }),
        event("defeat", "Defeat", "When Rocket League reports a defeat", {
          trackingName: "Defeat",
          aliases: ["defeat", "loss", "lost"],
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 7 },
        }),
        event("goal", "Goal", "When the player scores a goal", {
          trackingName: "Goal",
          aliases: ["goal", "goals", "personal goal"],
        }),
        event("action_point_assist", "Assist", "When the player assists a goal", {
          trackingName: "Assist",
          aliases: ["assist", "assists", "action point assist"],
        }),
        event("action_point_save", "Save", "When the player saves the goal", {
          trackingName: "Save",
          aliases: ["save", "saves", "action point save"],
        }),
        event("action_point_epic_save", "Epic Save", "When the player performs an epic save", {
          trackingName: "EpicSave",
          aliases: ["epic save", "action point epic save"],
        }),
        event("action_point_demolish", "Demolition", "When the player demolishes an opponent", {
          trackingName: "Demolition",
          aliases: ["demolition", "demolish", "demolished", "action point demolish"],
        }),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.CS2]: {
      gameId: GameIdsMap.CS2,
      gameName: "Counter-Strike 2",
      trackingName: "CS2",
      aliases: ["counter strike 2", "counter-strike 2", "cs2"],
      events: [
        matchStart("A Counter-Strike 2 match is started"),
        matchEnd(),
        kill(),
        assist(),
        death(),
        event("round_start", "Round Start", "When a new round starts", {
          trackingName: "RoundStart",
          aliases: ["round start", "round_start"],
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
        }),
        event("round_end", "Round End", "When a round ends", {
          trackingName: "RoundEnd",
          aliases: ["round end", "round_end"],
          defaults: { buffsReward: 0, duration: 10, triggerDelay: 0 },
        }),
        matchWon(false),
        matchLost(false),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.CALL_OF_DUTY]: {
      gameId: GameIdsMap.CALL_OF_DUTY,
      gameName: "Call of Duty",
      trackingName: "CallOfDuty",
      aliases: ["call of duty", "cod"],
      events: [
        matchStart("A Call of Duty match is started"),
        matchEnd(),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.REPO]: {
      gameId: GameIdsMap.REPO,
      gameName: "R.E.P.O.",
      trackingName: "REPO",
      aliases: ["repo", "r.e.p.o.", "r.e.p.o"],
      events: [
        matchStart("A R.E.P.O. match is started"),
        matchEnd(),
        timeElapsed15(),
      ],
    },
    [GameIdsMap.GENSHIN_IMPACT]: {
      gameId: GameIdsMap.GENSHIN_IMPACT,
      gameName: "Genshin Impact",
      trackingName: "Genshin",
      aliases: ["genshin", "genshin impact"],
      events: [
        matchStart("A Genshin Impact session starts"),
        matchEnd(),
        event("dungeon_start", "Dungeon Start", "At the beginning of a dungeon", {
          trackingName: "DungeonStart",
          aliases: ["dungeon start", "dungeon_start"],
        }),
        event("dungeon_end", "Dungeon End", "At the end of a dungeon", {
          trackingName: "DungeonEnd",
          aliases: ["dungeon end", "dungeon_end"],
        }),
        timeElapsed15(),
      ],
    },
  },
  timeBasedEvents: [launchFollowUp()],
}
